import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import './styles/index.css';

const CrearLiga = ({ onBack, onLigaCreada }) => {
  const [user] = useState(api.getCurrentUser());
  
  // Form data
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    maxParticipantes: '20',
    puntosPorVictoria: '3',
    puntosPorEmpate: '1',
    juegosParaGanar: '4',
    soloIda: false,
    asignarApuntadores: false,
    mismoDia: false, // Todas las partidas en el mismo día
    requierePago: true, // Si es false, no se necesita confirmar pago para ver clasificación
    modalidad: 'individual', // 'individual' o 'parejas'
  });
  
  // Fechas
  const [fechaInicio, setFechaInicio] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  // Búsqueda de usuarios
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Para parejas: equipos formados
  const [equipos, setEquipos] = useState([]);
  const [equipoEnFormacion, setEquipoEnFormacion] = useState({ jugador1: null, jugador2: null });
  
  // Estados
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [validacion, setValidacion] = useState(null);
  
  const esParejas = formData.modalidad === 'parejas';

  // Debounce para búsqueda
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Buscar usuarios
  const buscarUsuarios = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      setSearchLoading(true);
      const usuarios = await api.searchUsers(query);
      
      // Obtener IDs ya usados
      const idsUsados = new Set();
      selectedUsers.forEach(u => idsUsados.add(u._id));
      equipos.forEach(eq => {
        idsUsados.add(eq.jugador1._id);
        idsUsados.add(eq.jugador2._id);
      });
      if (equipoEnFormacion.jugador1) idsUsados.add(equipoEnFormacion.jugador1._id);
      if (equipoEnFormacion.jugador2) idsUsados.add(equipoEnFormacion.jugador2._id);
      
      // Filtrar usuarios ya usados y el propio bar
      const usuariosFiltrados = usuarios.filter(
        u => !idsUsados.has(u._id) && u._id !== user._id
      );
      setSearchResults(usuariosFiltrados);
    } catch (err) {
      console.error('Error buscando usuarios:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [selectedUsers, equipos, equipoEnFormacion, user._id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(buscarUsuarios, 300), [buscarUsuarios]);

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  // Validar configuración cuando cambia el número de participantes/equipos
  useEffect(() => {
    const validarConfig = async () => {
      const numCompetidores = esParejas ? equipos.length : selectedUsers.length;
      if (numCompetidores >= 2) {
        try {
          const result = await api.validarLiga(numCompetidores);
          setValidacion(result);
        } catch (err) {
          console.error('Error validando:', err);
        }
      } else {
        setValidacion(null);
      }
    };
    validarConfig();
  }, [selectedUsers.length, equipos.length, esParejas]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  // Para individual: seleccionar jugador directamente
  const seleccionarUsuario = (usuario) => {
    if (esParejas) {
      // Para parejas: añadir al equipo en formación
      if (!equipoEnFormacion.jugador1) {
        setEquipoEnFormacion({ jugador1: usuario, jugador2: null });
      } else if (!equipoEnFormacion.jugador2) {
        setEquipoEnFormacion(prev => ({ ...prev, jugador2: usuario }));
      }
    } else {
      setSelectedUsers(prev => [...prev, usuario]);
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
  };
  
  // Confirmar equipo (parejas)
  const confirmarEquipo = () => {
    if (equipoEnFormacion.jugador1 && equipoEnFormacion.jugador2) {
      setEquipos(prev => [...prev, { 
        jugador1: equipoEnFormacion.jugador1, 
        jugador2: equipoEnFormacion.jugador2 
      }]);
      setEquipoEnFormacion({ jugador1: null, jugador2: null });
    }
  };
  
  // Cancelar equipo en formación
  const cancelarEquipoEnFormacion = () => {
    setEquipoEnFormacion({ jugador1: null, jugador2: null });
  };
  
  // Remover equipo
  const removerEquipo = (index) => {
    setEquipos(prev => prev.filter((_, i) => i !== index));
  };

  const removerUsuario = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u._id !== userId));
  };

  const validarFormulario = () => {
    if (!formData.nombre.trim()) {
      setError('El nombre de la liga es obligatorio');
      return false;
    }
    
    if (esParejas) {
      if (equipos.length < 2) {
        setError('Se necesitan al menos 2 equipos');
        return false;
      }
    } else {
      if (selectedUsers.length < 2) {
        setError('Se necesitan al menos 2 participantes');
        return false;
      }
    }
    
    if (!fechaInicio) {
      setError('La fecha de inicio es obligatoria');
      return false;
    }
    
    return true;
  };

  const crearLiga = async () => {
    if (!validarFormulario()) return;
    
    try {
      setLoading(true);
      setError('');
      
      const ligaData = {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || `Liga creada por ${user.nombre}`,
        modalidad: formData.modalidad,
        bar: user._id || user.id,
        fechaInicio: new Date(fechaInicio).toISOString(),
        configuracion: {
          puntosPorVictoria: parseInt(formData.puntosPorVictoria),
          puntosPorEmpate: parseInt(formData.puntosPorEmpate),
          maxParticipantes: parseInt(formData.maxParticipantes),
          juegosParaGanar: parseInt(formData.juegosParaGanar),
          // Para parejas: forzar valores fijos
          soloIda: esParejas ? true : formData.soloIda,
          asignarApuntadores: esParejas ? true : formData.asignarApuntadores,
          mismoDia: esParejas ? true : formData.mismoDia,
          requierePago: esParejas ? false : formData.requierePago,
        }
      };
      
      // Añadir participantes o equipos según modalidad
      if (esParejas) {
        ligaData.equipos = equipos.map(eq => ({
          jugador1Id: eq.jugador1._id,
          jugador2Id: eq.jugador2._id
        }));
      } else {
        ligaData.participantes = selectedUsers.map(u => u._id);
      }

      // 1. Crear la liga
      const resultadoLiga = await api.crearLiga(ligaData);
      
      // 2. Generar calendario
      const resultadoCalendario = await api.generarCalendario(resultadoLiga.liga._id);
      
      // 3. Activar la liga
      await api.activarLiga(resultadoLiga.liga._id);
      
      setSuccess(`¡Liga "${resultadoLiga.liga.nombre}" creada exitosamente! Se generaron ${resultadoCalendario.partidasCreadas} partidas en ${resultadoCalendario.estadisticas.totalJornadas} jornadas.`);
      
      // Notificar al padre y volver después de un delay
      setTimeout(() => {
        if (onLigaCreada) onLigaCreada(resultadoLiga.liga);
        if (onBack) onBack();
      }, 2000);
      
    } catch (err) {
      setError(err.message || 'Error al crear la liga');
    } finally {
      setLoading(false);
    }
  };

  // Calcular estadísticas estimadas y fecha fin
  const calcularEstadisticas = () => {
    const n = esParejas ? equipos.length : selectedUsers.length;
    if (n < 2) return null;
    
    // Parejas siempre es soloIda
    const esRealmenteSoloIda = esParejas || formData.soloIda;
    
    const partidasTotales = esRealmenteSoloIda 
      ? (n * (n - 1)) / 2 
      : n * (n - 1);
    
    const jornadas = n % 2 === 0 
      ? (esRealmenteSoloIda ? n - 1 : (n - 1) * 2)
      : (esRealmenteSoloIda ? n : n * 2);
    
    // Calcular fecha fin estimada
    let fechaFinEstimada = null;
    if (fechaInicio) {
      const inicio = new Date(fechaInicio);
      // Parejas siempre es mismoDia
      if (formData.mismoDia || esParejas) {
        // Todas las jornadas el mismo día
        fechaFinEstimada = inicio;
      } else {
        // Una jornada por semana
        inicio.setDate(inicio.getDate() + (jornadas - 1) * 7);
        fechaFinEstimada = inicio;
      }
    }
    
    return { partidasTotales, jornadas, fechaFinEstimada, numCompetidores: n };
  };

  const estadisticas = calcularEstadisticas();

  return (
    <div className="crear-liga-container">
      {/* Header */}
      <div className="crear-liga-header">
        <button className="back-button" onClick={onBack}>
          ← Volver
        </button>
        <h1>🏆 Crear Nueva Liga</h1>
        <p>Crea una competición profesional de dardos</p>
      </div>

      {/* Mensajes */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="crear-liga-form">
        {/* Información básica */}
        <section className="form-section">
          <h2>📋 Información Básica</h2>
          
          {/* Selector de modalidad */}
          <div className="form-group">
            <label>Modalidad de la Liga</label>
            <div className="modalidad-selector">
              <button
                type="button"
                className={`modalidad-btn ${formData.modalidad === 'individual' ? 'active' : ''}`}
                onClick={() => {
                  handleInputChange('modalidad', 'individual');
                  setEquipos([]);
                  setEquipoEnFormacion({ jugador1: null, jugador2: null });
                }}
              >
                <span className="modalidad-icon">👤</span>
                <span className="modalidad-text">Individual</span>
              </button>
              <button
                type="button"
                className={`modalidad-btn ${formData.modalidad === 'parejas' ? 'active' : ''}`}
                onClick={() => {
                  handleInputChange('modalidad', 'parejas');
                  setSelectedUsers([]);
                }}
              >
                <span className="modalidad-icon">👥</span>
                <span className="modalidad-text">Parejas</span>
              </button>
            </div>
          </div>
          
          <div className="form-group">
            <label>Nombre de la Liga *</label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => handleInputChange('nombre', e.target.value)}
              placeholder={esParejas ? "Ej: Liga Parejas Invierno 2026" : "Ej: Liga Primavera 2026"}
              maxLength={50}
            />
          </div>
          
          <div className="form-group">
            <label>Descripción</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => handleInputChange('descripcion', e.target.value)}
              placeholder="Descripción opcional de la liga..."
              rows={3}
              maxLength={200}
            />
          </div>
        </section>

        {/* Sección de Equipos (solo parejas) */}
        {esParejas && (
          <section className="form-section">
            <h2>👥 Equipos ({equipos.length})</h2>
            
            {/* Equipos formados */}
            {equipos.length > 0 && (
              <div className="equipos-list">
                {equipos.map((equipo, index) => (
                  <div key={index} className="equipo-card">
                    <div className="equipo-numero">#{index + 1}</div>
                    <div className="equipo-jugadores">
                      <span className="equipo-jugador">
                        @{equipo.jugador1.username}
                      </span>
                      <span className="equipo-separator">/</span>
                      <span className="equipo-jugador">
                        @{equipo.jugador2.username}
                      </span>
                    </div>
                    <button 
                      className="remove-equipo"
                      onClick={() => removerEquipo(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Equipo en formación */}
            <div className="equipo-formacion">
              <h4>➕ Nuevo Equipo</h4>
              <div className="equipo-formacion-slots">
                <div className={`slot ${equipoEnFormacion.jugador1 ? 'filled' : 'empty'}`}>
                  {equipoEnFormacion.jugador1 ? (
                    <span>@{equipoEnFormacion.jugador1.username}</span>
                  ) : (
                    <span className="slot-placeholder">Jugador 1</span>
                  )}
                </div>
                <span className="slot-separator">/</span>
                <div className={`slot ${equipoEnFormacion.jugador2 ? 'filled' : 'empty'}`}>
                  {equipoEnFormacion.jugador2 ? (
                    <span>@{equipoEnFormacion.jugador2.username}</span>
                  ) : (
                    <span className="slot-placeholder">Jugador 2</span>
                  )}
                </div>
              </div>
              
              {(equipoEnFormacion.jugador1 || equipoEnFormacion.jugador2) && (
                <div className="equipo-formacion-actions">
                  {equipoEnFormacion.jugador1 && equipoEnFormacion.jugador2 && (
                    <button className="btn-confirmar-equipo" onClick={confirmarEquipo}>
                      ✓ Confirmar Equipo
                    </button>
                  )}
                  <button className="btn-cancelar-equipo" onClick={cancelarEquipoEnFormacion}>
                    Cancelar
                  </button>
                </div>
              )}
            </div>
            
            {/* Buscador para parejas */}
            <div className="search-container">
              <div className="form-group">
                <label>Buscar jugadores para el equipo</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Buscar por username..."
                />
                {searchLoading && <span className="search-loading">Buscando...</span>}
              </div>
              
              {/* Dropdown de resultados */}
              {showDropdown && searchResults.length > 0 && (
                <div className="search-dropdown">
                  {searchResults.map((usuario) => (
                    <div 
                      key={usuario._id} 
                      className="search-result"
                      onClick={() => seleccionarUsuario(usuario)}
                    >
                      <span className="result-avatar">
                        {usuario.username.charAt(0).toUpperCase()}
                      </span>
                      <div className="result-info">
                        <span className="result-username">@{usuario.username}</span>
                        <span className="result-nombre">{usuario.nombre}</span>
                      </div>
                      <span className="add-icon">+</span>
                    </div>
                  ))}
                </div>
              )}
              
              {showDropdown && searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
                <div className="no-results">
                  No se encontraron jugadores
                </div>
              )}
            </div>
          </section>
        )}

        {/* Participantes (solo individual) */}
        {!esParejas && (
        <section className="form-section">
          <h2>👥 Participantes ({selectedUsers.length})</h2>
          
          {/* Usuarios seleccionados */}
          {selectedUsers.length > 0 && (
            <div className="selected-users">
              {selectedUsers.map((usuario) => (
                <div key={usuario._id} className="user-chip">
                  <span className="user-avatar">
                    {usuario.username.charAt(0).toUpperCase()}
                  </span>
                  <div className="user-info">
                    <span className="username">@{usuario.username}</span>
                    <span className="nombre">{usuario.nombre}</span>
                  </div>
                  <button 
                    className="remove-user"
                    onClick={() => removerUsuario(usuario._id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Buscador */}
          <div className="search-container">
            <div className="form-group">
              <label>Buscar jugadores</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Buscar por username..."
              />
              {searchLoading && <span className="search-loading">Buscando...</span>}
            </div>
            
            {/* Dropdown de resultados */}
            {showDropdown && searchResults.length > 0 && (
              <div className="search-dropdown">
                {searchResults.map((usuario) => (
                  <div 
                    key={usuario._id} 
                    className="search-result"
                    onClick={() => seleccionarUsuario(usuario)}
                  >
                    <span className="result-avatar">
                      {usuario.username.charAt(0).toUpperCase()}
                    </span>
                    <div className="result-info">
                      <span className="result-username">@{usuario.username}</span>
                      <span className="result-nombre">{usuario.nombre}</span>
                    </div>
                    <span className="add-icon">+</span>
                  </div>
                ))}
              </div>
            )}
            
            {showDropdown && searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
              <div className="no-results">
                No se encontraron jugadores
              </div>
            )}
          </div>
        </section>
        )}

        {/* Calendario */}
        <section className="form-section">
          <h2>📅 Calendario</h2>
          
          <div className="date-row">
            <div className="form-group">
              <label>Fecha de Inicio *</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div className="form-group">
              <label>Fecha de Fin (calculada)</label>
              <div className="fecha-calculada">
                {estadisticas?.fechaFinEstimada 
                  ? estadisticas.fechaFinEstimada.toLocaleDateString('es-ES', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })
                  : 'Selecciona participantes'
                }
              </div>
            </div>
          </div>

          {/* Toggle mismo día - Solo mostrar si no es parejas */}
          {formData.modalidad !== 'parejas' && (
            <div 
              className={`formato-toggle ${formData.mismoDia ? 'active' : ''}`}
              onClick={() => handleInputChange('mismoDia', !formData.mismoDia)}
              style={{ marginTop: '1rem' }}
            >
              <div className="formato-info">
                <span className="formato-title">
                  {formData.mismoDia ? '📍 Todo en un día' : '📆 Una jornada por semana'}
                </span>
                <span className={`formato-badge ${formData.mismoDia ? 'rapido' : 'estandar'}`}>
                  {formData.mismoDia ? 'TORNEO' : 'LIGA'}
                </span>
                <p className="formato-desc">
                  {formData.mismoDia 
                    ? 'Todas las jornadas se juegan el mismo día • Ideal para campeonatos con liguillas'
                    : 'Una jornada cada semana • Formato liga tradicional'}
                </p>
              </div>
              <div className={`toggle-switch ${formData.mismoDia ? 'on' : ''}`}>
                <div className="toggle-circle"></div>
              </div>
            </div>
          )}
        </section>

        {/* Formato de Liga */}
        <section className="form-section">
          <h2>🔄 Formato de Liga</h2>
          
          {/* Aviso para ligas de parejas */}
          {formData.modalidad === 'parejas' && (
            <div className="parejas-info-box">
              <div className="parejas-info-icon">👥</div>
              <div className="parejas-info-content">
                <span className="parejas-info-title">Formato Liga de Parejas</span>
                <p className="parejas-info-desc">
                  Las ligas de parejas se juegan en un único día con formato solo ida. 
                  Cada partida tendrá un apuntador asignado automáticamente y la clasificación 
                  se actualiza instantáneamente.
                </p>
                <div className="parejas-info-badges">
                  <span className="parejas-badge">📍 Mismo día</span>
                  <span className="parejas-badge">⚡ Solo ida</span>
                  <span className="parejas-badge">✍️ Con apuntadores</span>
                  <span className="parejas-badge">🚀 Sin confirmación</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Solo Ida - Solo mostrar si no es parejas */}
          {formData.modalidad !== 'parejas' && (
            <div 
              className={`formato-toggle ${formData.soloIda ? 'active' : ''}`}
              onClick={() => handleInputChange('soloIda', !formData.soloIda)}
            >
              <div className="formato-info">
                <span className="formato-title">
                  {formData.soloIda ? '⚡ Solo Ida' : '🔄 Ida y Vuelta'}
                </span>
                <span className={`formato-badge ${formData.soloIda ? 'rapido' : 'estandar'}`}>
                  {formData.soloIda ? 'RÁPIDO' : 'ESTÁNDAR'}
                </span>
                <p className="formato-desc">
                  {formData.soloIda 
                    ? 'Cada pareja juega 1 vez • Ideal para muchos jugadores'
                    : 'Cada pareja juega 2 veces • Formato tradicional'}
                </p>
              </div>
              <div className={`toggle-switch ${formData.soloIda ? 'on' : ''}`}>
                <div className="toggle-circle"></div>
              </div>
            </div>
          )}

          {/* Opción de apuntadores - Solo mostrar si no es parejas */}
          {formData.modalidad !== 'parejas' && (
            <div 
              className={`formato-toggle ${formData.asignarApuntadores ? 'active' : ''}`}
              onClick={() => handleInputChange('asignarApuntadores', !formData.asignarApuntadores)}
              style={{ marginTop: '1rem' }}
            >
              <div className="formato-info">
                <span className="formato-title">
                  {formData.asignarApuntadores ? '✍️ Con Apuntadores' : '📝 Sin Apuntadores'}
                </span>
                <span className={`formato-badge ${formData.asignarApuntadores ? 'rapido' : 'estandar'}`}>
                  {formData.asignarApuntadores ? 'LIGUILLA' : 'NORMAL'}
                </span>
                <p className="formato-desc">
                  {formData.asignarApuntadores 
                    ? 'Asigna automáticamente quién apunta cada partida • Ideal para liguillas pequeñas'
                    : 'Sin asignación de apuntadores • Formato estándar'}
                </p>
              </div>
              <div className={`toggle-switch ${formData.asignarApuntadores ? 'on' : ''}`}>
                <div className="toggle-circle"></div>
              </div>
            </div>
          )}

          {/* Toggle Requiere Pago - Solo mostrar si no es parejas */}
          {formData.modalidad !== 'parejas' && (
            <div 
              className={`formato-toggle ${!formData.requierePago ? 'active' : ''}`}
              onClick={() => handleInputChange('requierePago', !formData.requierePago)}
            >
              <div className="formato-info">
                <span className="formato-title">
                  {formData.requierePago ? '💰 Con Confirmación de Pago' : '⚡ Sin Pago Requerido'}
                </span>
                <span className={`formato-badge ${!formData.requierePago ? 'rapido' : 'estandar'}`}>
                  {formData.requierePago ? 'ESTÁNDAR' : 'INSTANTÁNEO'}
                </span>
                <p className="formato-desc">
                  {formData.requierePago 
                    ? 'El bar debe confirmar el pago para que cuente en la clasificación'
                    : 'La clasificación se actualiza inmediatamente al terminar la partida'}
                </p>
              </div>
              <div className={`toggle-switch ${!formData.requierePago ? 'on' : ''}`}>
                <div className="toggle-circle"></div>
              </div>
            </div>
          )}

          {/* Estadísticas estimadas */}
          {estadisticas && (
            <div className="estadisticas-preview">
              <div className="stat">
                <span className="stat-value">{estadisticas.partidasTotales}</span>
                <span className="stat-label">Partidas</span>
              </div>
              <div className="stat">
                <span className="stat-value">{estadisticas.jornadas}</span>
                <span className="stat-label">Jornadas</span>
              </div>
              <div className="stat">
                <span className="stat-value">{selectedUsers.length}</span>
                <span className="stat-label">Jugadores</span>
              </div>
            </div>
          )}
        </section>

        {/* Configuración de puntos */}
        <section className="form-section">
          <h2>⚙️ Configuración</h2>
          
          <div className="config-grid">
            <div className="form-group">
              <label>Juegos para ganar</label>
              <select
                value={formData.juegosParaGanar}
                onChange={(e) => handleInputChange('juegosParaGanar', e.target.value)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <option key={n} value={n}>Al mejor de {n * 2 - 1} ({n} para ganar)</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Puntos por Victoria</label>
              <input
                type="number"
                value={formData.puntosPorVictoria}
                onChange={(e) => handleInputChange('puntosPorVictoria', e.target.value)}
                min={1}
                max={10}
              />
            </div>
            
            <div className="form-group">
              <label>Puntos por Derrota</label>
              <input
                type="number"
                value={formData.puntosPorEmpate}
                onChange={(e) => handleInputChange('puntosPorEmpate', e.target.value)}
                min={0}
                max={5}
              />
            </div>
          </div>
        </section>

        {/* Botón crear */}
        <div className="form-actions">
          <button 
            className="btn-secondary"
            onClick={onBack}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            className="btn-primary"
            onClick={crearLiga}
            disabled={loading || (esParejas ? equipos.length < 2 : selectedUsers.length < 2)}
          >
            {loading ? 'Creando...' : '🏆 Crear Liga'}
          </button>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div 
          className="dropdown-overlay"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default CrearLiga;
